import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import { api } from "../lib/api";
import {
  Loader2, AlertTriangle, X, FileDown, Database, Globe, TrafficCone,
  Target, Layers, TrendingUp, TrendingDown, Minus,
} from "lucide-react";

const SEV_COLOR = {
  high:   "var(--auditiq-critical, #e85b5b)",
  medium: "var(--auditiq-warning, #e8a85b)",
  low:    "var(--auditiq-success, #5bbf85)",
};

const SOURCE_META = {
  csv_upload:           { label: "CSV Upload",           Icon: Database },
  ai_crawler:           { label: "AI Crawler",           Icon: Globe },
  traffic_intelligence: { label: "Traffic Intelligence", Icon: TrafficCone },
};

function SourceBadge({ source }) {
  const m = SOURCE_META[source] || { label: source || "Audit", Icon: Database };
  const Icon = m.Icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em]"
      style={{
        color: "#C9A84C",
        backgroundColor: "rgba(201,168,76,0.08)",
        border: "1px solid rgba(201,168,76,0.4)",
      }}
      data-testid={`source-badge-${source || "unknown"}`}
    >
      <Icon size={12} /> Source: {m.label}
    </span>
  );
}

function DeltaChip({ value, neutralAtZero = true, inverseColor = false }) {
  const v = Number(value || 0);
  if (neutralAtZero && v === 0) {
    return <span className="inline-flex items-center gap-0.5 text-[10px] text-cream-dim"><Minus size={10} /> 0</span>;
  }
  // For pillars/score: up=good (green). For risk/findings: up=bad (red).
  const isGood = inverseColor ? v < 0 : v > 0;
  const color = v === 0 ? "#94A3B8" : isGood ? "#34D399" : "#F87171";
  const Icon = v > 0 ? TrendingUp : v < 0 ? TrendingDown : Minus;
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10px] font-semibold"
      style={{ color }}
      data-testid="delta-chip"
    >
      <Icon size={10} /> {v > 0 ? "+" : ""}{v.toFixed(1)}
    </span>
  );
}

function PillarTile({ label, value, delta }) {
  const v = Number(value || 0);
  const color = v >= 85 ? "#5bbf85" : v >= 70 ? "#2D9CDB" : v >= 55 ? "#e8a85b" : "#e85b5b";
  return (
    <div className="glass p-3 rounded-md border border-gold-dim/20" data-testid={`pillar-${label}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-cream-dim">{label}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <div className="text-2xl font-display" style={{ color }}>{v.toFixed(1)}</div>
        {delta !== undefined && delta !== null && (
          <DeltaChip value={delta} />
        )}
      </div>
      <div className="h-1 rounded-full mt-2" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
        <div
          className="h-1 rounded-full transition-[width]"
          style={{ width: `${Math.max(0, Math.min(100, v))}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function FivePillarsPanel({ fp, whatChanged }) {
  if (!fp) return null;
  const pd = whatChanged?.pillarDeltas || {};
  const overallDelta = whatChanged?.overallScoreDelta;
  const cert = whatChanged?.certificationChange;
  const hasCertChange = cert && cert.from && cert.to && cert.from !== cert.to;
  return (
    <section className="card-premium glass p-5 space-y-3" data-testid="panel-five-pillars">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Layers size={16} className="text-electric" />
          <h3 className="font-display text-xl text-cream">Five Pillars</h3>
          <span className="text-xs text-cream-dim">0-100, higher is healthier</span>
          {whatChanged?.previousCrawlId && (
            <span className="text-[10px] text-cream-dim italic ml-2" data-testid="pillars-since-prior">
              vs prior scan
              {whatChanged.previousCrawlDate ? ` · ${String(whatChanged.previousCrawlDate).slice(0, 10)}` : ""}
            </span>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] text-cream-dim uppercase tracking-wider">Overall</div>
          <div className="flex items-baseline gap-2 justify-end">
            <div className="text-2xl font-display text-gold">{Number(fp.overall_score || 0).toFixed(1)}</div>
            {overallDelta !== undefined && overallDelta !== null && (
              <DeltaChip value={overallDelta} />
            )}
          </div>
          <div className="text-[10px] text-electric uppercase tracking-wider">
            {fp.certification_level || "—"}
            {hasCertChange && (
              <span className="ml-2 text-amber-300" data-testid="pillars-cert-change">
                (was {cert.from})
              </span>
            )}
          </div>
        </div>
      </header>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <PillarTile label="Financial Transparency" value={fp.financial_transparency} delta={pd.financial_transparency} />
        <PillarTile label="Infrastructure Risk"    value={fp.infrastructure_risk}    delta={pd.infrastructure_risk} />
        <PillarTile label="Cyber Readiness"        value={fp.cyber_readiness}        delta={pd.cyber_readiness} />
        <PillarTile label="Operational Efficiency" value={fp.operational_efficiency} delta={pd.operational_efficiency} />
        <PillarTile label="Public Trust"           value={fp.public_trust}           delta={pd.public_trust} />
      </div>
    </section>
  );
}

function HawkinsPanel({ hawkins }) {
  if (!hawkins) return null;
  const visible = hawkins.visible || hawkins.visible_signals || [];
  const hidden = hawkins.hidden || hawkins.hidden_signals || [];
  const core = hawkins.gravitational_core || hawkins.core || "";
  return (
    <section className="card-premium glass p-5 space-y-3" data-testid="panel-hawkins">
      <header className="flex items-center gap-2">
        <Target size={16} className="text-electric" />
        <h3 className="font-display text-xl text-cream">Hawkins 3+3+1</h3>
        <span className="text-xs text-cream-dim">visible · hidden · gravitational core</span>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-emerald-400 mb-2">Visible (3)</div>
          <ul className="space-y-1 text-sm text-cream-dim">
            {(Array.isArray(visible) ? visible : []).slice(0, 3).map((v, i) => (
              <li key={i} className="leading-relaxed">• {typeof v === "string" ? v : v.text || v.label}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-2">Hidden (3)</div>
          <ul className="space-y-1 text-sm text-cream-dim">
            {(Array.isArray(hidden) ? hidden : []).slice(0, 3).map((v, i) => (
              <li key={i} className="leading-relaxed">• {typeof v === "string" ? v : v.text || v.label}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[color:#C9A84C] mb-2">Gravitational Core (1)</div>
          <div className="text-sm text-cream leading-relaxed border-l-2 border-gold/60 pl-3">
            {typeof core === "string" ? core : (core?.text || core?.label || "—")}
          </div>
        </div>
      </div>
    </section>
  );
}

const FindingDrawer = ({ finding, onClose }) => {
  if (!finding) return null;
  const sev = (finding.severity || "").toLowerCase();
  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/60 z-40 transition-opacity"
        data-testid="finding-drawer-backdrop"
      />
      <aside
        className="fixed top-0 right-0 h-full w-full sm:w-[460px] bg-navy-deep border-l border-gold-dim/40 z-50 overflow-y-auto"
        style={{ animation: "slideIn .25s ease-out" }}
        data-testid="finding-drawer"
      >
        <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
        <div className="p-6 space-y-5">
          <div className="flex items-start justify-between gap-3">
            <div className="text-[10px] uppercase tracking-[0.24em] text-electric">Finding Detail</div>
            <button onClick={onClose} className="text-cream-dim hover:text-gold" data-testid="finding-drawer-close">
              <X size={16} />
            </button>
          </div>
          <h2 className="font-display text-2xl text-cream">{finding.title}</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            <span
              className="px-2 py-0.5 rounded-full font-semibold"
              style={{ backgroundColor: `${SEV_COLOR[sev] || "#888"}22`, color: SEV_COLOR[sev] || "#aaa" }}
            >
              {finding.severity}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-navy-soft border border-gold-dim/30 text-cream-dim">
              {finding.category}
            </span>
          </div>
          {finding.notes && (
            <div className="pt-2 border-t border-gold-dim/20">
              <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim mb-2">Notes</div>
              <p className="text-sm text-cream-dim leading-relaxed">{finding.notes}</p>
            </div>
          )}
          <div className="pt-2 border-t border-gold-dim/20">
            <div className="text-[10px] uppercase tracking-[0.2em] text-gold mb-2">Recommendation</div>
            <p className="text-sm text-cream leading-relaxed">{finding.recommendation || "—"}</p>
          </div>
        </div>
      </aside>
    </>
  );
};

const AuditDashboard = () => {
  const [params] = useSearchParams();
  const auditId = params.get("audit_id");
  const crawlId = params.get("crawl_id");
  const activeId = auditId || crawlId;
  const iframeRef = useRef(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [iframeReady, setIframeReady] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [drawerFinding, setDrawerFinding] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const source = data?.source || (crawlId ? "ai_crawler" : "csv_upload");

  const downloadPdf = async () => {
    if (!auditId) return;
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/auditiq/report/${auditId}.pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `auditiq-report-${auditId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || "PDF download failed");
    }
    setDownloadingPdf(false);
  };

  // Fetch dashboard data — supports both audit_id and crawl_id
  useEffect(() => {
    if (!activeId) {
      setError("Missing audit_id or crawl_id query parameter.");
      return;
    }
    const url = crawlId
      ? `/auditiq/dashboard/crawl/${crawlId}`
      : `/auditiq/dashboard/${auditId}`;
    api.get(url)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Failed to load dashboard"));
  }, [activeId, auditId, crawlId]);

  // Fetch org name for header (audits only)
  useEffect(() => {
    if (!auditId) {
      if (crawlId) setOrgName("AI Crawler Scan");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const jobsRes = await api.get("/auditiq/jobs");
        const job = (jobsRes.data?.jobs || []).find(j => j.audit_id === auditId);
        if (!job) return;
        const orgsRes = await api.get("/portal/organizations");
        const org = (orgsRes.data?.organizations || []).find(x => x.id === job.organization_id);
        if (!cancelled && org) setOrgName(org.name);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [auditId, crawlId]);

  useEffect(() => {
    const onMsg = (e) => {
      if (!e?.data) return;
      if (e.data.type === "dashboardReady") setIframeReady(true);
      if (e.data.type === "findingClick" && e.data.finding) setDrawerFinding(e.data.finding);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => {
    if (!data || !iframeRef.current || !iframeReady) return;
    const t = setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage({ type: "dashboardData", ...data }, "*");
    }, 300);
    return () => clearTimeout(t);
  }, [data, iframeReady]);

  return (
    <PortalLayout>
      <div className="flex flex-col" style={{ height: "calc(100vh - 0px)" }}>
        <div className="px-1 pb-3 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[11px] uppercase tracking-[0.28em] text-electric">AuditIQ Dashboard</div>
              <SourceBadge source={source} />
            </div>
            <h1
              className="font-display text-3xl md:text-4xl mt-1 text-gold"
              data-testid="auditiq-dashboard-orgname"
            >
              {orgName || (crawlId ? "AI Crawler Scan" : "Audit Dashboard")}
            </h1>
            <p className="text-xs text-cream-dim mt-1">
              {auditId ? "Audit ID: " : "Crawl ID: "}
              <span className="text-gold/80 font-mono">{activeId || "—"}</span>
              {data?.industry && (
                <>
                  {" · "}
                  <span className="capitalize text-electric" data-testid="auditiq-dashboard-industry">
                    {String(data.industry).replace(/_/g, " ")}
                  </span>
                </>
              )}
              {" · "}
              <Link to="/auditiq/history" className="text-electric hover:text-gold underline-offset-2 hover:underline">
                View history
              </Link>
            </p>
          </div>
          {data && auditId && (
            <button
              onClick={downloadPdf}
              disabled={downloadingPdf}
              className="btn-gold h-10 px-4 inline-flex items-center gap-2 text-sm rounded-md disabled:opacity-60"
              data-testid="auditiq-download-pdf-btn"
            >
              {downloadingPdf
                ? <><Loader2 size={14} className="animate-spin" /> Generating…</>
                : <><FileDown size={14} /> Download PDF</>}
            </button>
          )}
        </div>

        {error && (
          <div className="card-premium glass border-l-4 border-[var(--auditiq-critical)]" data-testid="auditiq-dashboard-error">
            <div className="flex items-center gap-2 text-cream">
              <AlertTriangle size={16} /> {error}
            </div>
          </div>
        )}

        {!error && !data && (
          <div className="card-premium glass flex items-center gap-2 text-cream-dim" data-testid="auditiq-dashboard-loading">
            <Loader2 className="animate-spin" size={16} /> Loading audit dashboard…
          </div>
        )}

        {data && (
          <>
            {/* Five Pillars + Hawkins rendered natively when crawler source */}
            {data.fivePillars && (
              <div className="mb-3">
                <FivePillarsPanel fp={data.fivePillars} whatChanged={data.whatChanged} />
              </div>
            )}
            {data.hawkins && (
              <div className="mb-3">
                <HawkinsPanel hawkins={data.hawkins} />
              </div>
            )}

            <div
              className="flex-1 overflow-hidden bg-navy-deep border border-gold-dim/25 rounded-md"
              style={{ minHeight: "1600px" }}
              data-testid="auditiq-dashboard-frame"
            >
              <iframe
                ref={iframeRef}
                src="/dashboard.html"
                title="AuditIQ Dashboard"
                style={{ width: "100%", height: "100%", minHeight: "1600px", border: 0, display: "block", background: "var(--navy, #0d1f3c)" }}
              />
            </div>
          </>
        )}
      </div>

      <FindingDrawer finding={drawerFinding} onClose={() => setDrawerFinding(null)} />
    </PortalLayout>
  );
};

export default AuditDashboard;
